import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pill, CheckSquare, ShoppingCart, ArrowUpRight, Camera, Bell, FileDown, StickyNote, Ruler, QrCode, Image as ImageIcon, CalendarDays, Flame, ShieldCheck, Layers, Receipt, ListChecks, FileText, ScanText, Brain } from "lucide-react";
import InstallBanner from "../components/InstallBanner";
import AdSlot from "../components/AdSlot";
import LifeInboxInput from "../components/LifeInboxInput";
import InsightsCard from "../components/InsightsCard";
import SyncNudgeCard from "../components/SyncNudgeCard";
import FeatureGate from "../components/FeatureGate";
import { rx } from "../lib/api";

const Stat = ({ k, v }) => (
  <div className="flex flex-col gap-1">
    <span className="label-caps">{k}</span>
    <span className="font-display font-bold text-xl text-[color:var(--text-main)]">{v}</span>
  </div>
);

const MiniTile = ({ to, icon: Icon, bg, ig, title, desc, testid }) => (
  <Link to={to} data-testid={testid} className="card-tactile rounded-2xl p-5 flex flex-col justify-between group min-h-[150px]">
    <div>
      <div className={`h-10 w-10 rounded-xl ${bg} flex items-center justify-center mb-3`}>
        <Icon className={`h-5 w-5 ${ig}`} />
      </div>
      <h3 className="font-display font-bold text-lg tracking-tight">{title}</h3>
      <p className="text-sm text-[color:var(--text-muted)] mt-1">{desc}</p>
    </div>
    <div className="flex items-center justify-between text-sm text-[color:var(--primary-ink)] font-semibold mt-4">
      Open <ArrowUpRight className="h-4 w-4" />
    </div>
  </Link>
);

export default function HubPage() {
  const [rxList, setRxList] = useState([]);

  useEffect(() => {
    rx.list().then(setRxList).catch(() => {});
  }, []);

  // Compute aggregate: best streak, weighted adherence across prescriptions with past doses
  const withPast = rxList.filter(r => r.adherence && r.adherence.total_past > 0);
  const totalPast = withPast.reduce((a, r) => a + r.adherence.total_past, 0);
  const totalTaken = withPast.reduce((a, r) => a + r.adherence.taken, 0);
  const overallPct = totalPast ? Math.round((totalTaken / totalPast) * 100) : 0;
  const bestStreak = withPast.reduce((a, r) => Math.max(a, r.adherence.current_streak), 0);
  const activeCourses = rxList.length;

  return (
    <div className="animate-fade-up">
      <InstallBanner />
      <section className="mb-10 md:mb-14">
        <span className="label-caps" data-testid="hub-eyebrow">Toolkit · No clutter</span>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter text-[color:var(--text-main)] max-w-3xl">
          A quiet set of tools for the small, stubborn tasks of daily life.
        </h1>
        <p className="mt-4 max-w-2xl text-base sm:text-lg text-[color:var(--text-muted)] leading-relaxed">
          Manage prescriptions with AI-read labels and smart reminders. Keep a clean to-do, shopping list, and notes. Generate calendars and QR codes on demand. No accounts, no noise.
        </p>
        <div
          className="mt-5 inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm rounded-full border px-4 py-2"
          style={{ background: "color-mix(in srgb, var(--primary) 7%, transparent)", borderColor: "color-mix(in srgb, var(--primary) 25%, var(--border-subtle))" }}
          data-testid="trust-badge"
        >
          <ShieldCheck className="h-4 w-4 shrink-0" style={{ color: "var(--primary)" }} />
          <span style={{ color: "var(--text-muted)" }}>
            Your data stays tied to this device.
          </span>
          <Link to="/privacy" className="underline hover:no-underline" data-testid="trust-badge-privacy" style={{ color: "var(--primary)" }}>
            Privacy notice
          </Link>
          <span style={{ color: "var(--text-muted)" }} aria-hidden="true">·</span>
          <Link to="/settings#forget-device" className="underline hover:no-underline" data-testid="trust-badge-forget" style={{ color: "var(--primary)" }}>
            Forget this device
          </Link>
        </div>
      </section>

      <FeatureGate flag="feature_ai_insights">
        <section className="mb-8">
          <InsightsCard />
        </section>
      </FeatureGate>

      <section className="mb-8">
        <SyncNudgeCard />
      </section>

      <FeatureGate flag="feature_life_inbox">
        <section className="mb-10">
          <LifeInboxInput />
          <div className="mt-2 text-right">
            <Link to="/inbox" className="text-xs font-semibold hover:underline" data-testid="hub-inbox-link" style={{ color: "var(--primary-ink)" }}>
              Open full inbox →
            </Link>
          </div>
        </section>
      </FeatureGate>

      <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6 lg:gap-8">
        {/* Prescription — flagship */}
        <FeatureGate flag="show_prescriptions">
        <Link
          to="/prescriptions"
          data-testid="tile-prescriptions"
          className="card-tactile rounded-2xl p-8 md:col-span-8 lg:col-span-12 relative overflow-hidden group flex flex-col justify-between min-h-[320px]"
        >
          <div className="absolute inset-0 pointer-events-none opacity-[0.05] bg-[radial-gradient(circle_at_10%_20%,#3F7A68_0%,transparent_45%),radial-gradient(circle_at_90%_80%,#C46E4E_0%,transparent_45%)]" />
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 items-end">
            <div>
              <div className="flex items-center gap-2 mb-5">
                <div className="h-11 w-11 rounded-xl bg-[color:var(--primary-soft)] flex items-center justify-center">
                  <Pill className="h-6 w-6 text-[color:var(--primary-ink)]" />
                </div>
                <span className="label-caps">Flagship Tool</span>
              </div>
              <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[color:var(--text-main)] leading-[1.05] max-w-xl">
                Prescription Manager.
              </h2>
              <p className="mt-3 text-[color:var(--text-muted)] max-w-2xl leading-relaxed">
                Snap a photo of any label. We read it, build your schedule, and nudge you before every dose. Export to your calendar or a printable sheet.
              </p>
            </div>
            <div className="flex flex-col gap-4 items-start lg:items-end">
              <div className="grid grid-cols-3 gap-6">
                {activeCourses > 0 ? (
                  <>
                    <Stat k="Active" v={<>{activeCourses} <span className="text-sm text-[color:var(--text-muted)]">course{activeCourses === 1 ? "" : "s"}</span></>} />
                    <Stat k="Adherence" v={<span data-testid="hub-adherence">{totalPast > 0 ? `${overallPct}%` : "—"}</span>} />
                    <Stat k={<span className="flex items-center gap-1"><Flame className="h-3 w-3 text-[color:var(--accent-ink)]" /> Streak</span>} v={<span data-testid="hub-streak">{bestStreak || "—"}</span>} />
                  </>
                ) : (
                  <>
                    <Stat k="AI Reads" v="Labels" />
                    <Stat k="Push" v="Reminders" />
                    <Stat k="Export" v="PDF · ICS" />
                  </>
                )}
              </div>
              <span className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[color:var(--primary)] text-white font-semibold text-sm group-hover:bg-[color:var(--primary-hover)] transition-colors">
                Open Manager <ArrowUpRight className="h-4 w-4" />
              </span>
            </div>
          </div>
        </Link>
        </FeatureGate>

        {/* 6 utility mini tiles in responsive grid */}
        <div className="md:col-span-8 lg:col-span-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureGate flag="show_todo"><MiniTile to="/todo" icon={CheckSquare} bg="bg-[color:var(--warning-bg)]" ig="text-[color:var(--warning)]" title="To-Do List" desc="Prioritize, categorize, close the loop." testid="tile-todo" /></FeatureGate>
          <FeatureGate flag="show_shopping"><MiniTile to="/shopping" icon={ShoppingCart} bg="bg-[color:var(--accent-soft)]" ig="text-[color:var(--accent-ink)]" title="Shopping List" desc="Grouped by aisle." testid="tile-shopping" /></FeatureGate>
          <FeatureGate flag="show_notes"><MiniTile to="/notes" icon={StickyNote} bg="bg-[color:var(--primary-soft)]" ig="text-[color:var(--primary-ink)]" title="Notes" desc="Quick thoughts, tagged." testid="tile-notes" /></FeatureGate>
          <FeatureGate flag="feature_routines"><MiniTile to="/routines" icon={Layers} bg="bg-[color:var(--primary-soft)]" ig="text-[color:var(--primary-ink)]" title="Routines" desc="Morning · evening rituals." testid="tile-routines" /></FeatureGate>
          <FeatureGate flag="feature_subscriptions"><MiniTile to="/life-admin" icon={Receipt} bg="bg-[color:var(--accent-soft)]" ig="text-[color:var(--accent-ink)]" title="Life Admin" desc="Subs · bills · renewals." testid="tile-life-admin" /></FeatureGate>
          <FeatureGate flag="show_calendar_generator"><MiniTile to="/tools/calendar-generator" icon={CalendarDays} bg="bg-[color:var(--warning-bg)]" ig="text-[color:var(--warning)]" title="Calendar Generator" desc="Printable month PDF." testid="tile-calendar-gen" /></FeatureGate>
          <FeatureGate flag="show_unit_converter"><MiniTile to="/tools/unit-converter" icon={Ruler} bg="bg-[color:var(--accent-soft)]" ig="text-[color:var(--accent-ink)]" title="Unit Converter" desc="Length · weight · volume · temp." testid="tile-unit-conv" /></FeatureGate>
          <FeatureGate flag="show_image_editor"><MiniTile to="/tools/image-editor" icon={ImageIcon} bg="bg-[color:var(--warning-bg)]" ig="text-[color:var(--warning)]" title="Image Editor" desc="Convert · compress · resize." testid="tile-image-editor" /></FeatureGate>
          <FeatureGate flag="show_qr_generator"><MiniTile to="/tools/qr-generator" icon={QrCode} bg="bg-[color:var(--primary-soft)]" ig="text-[color:var(--primary-ink)]" title="QR Generator" desc="Text or URL to PNG." testid="tile-qr-gen" /></FeatureGate>
          <FeatureGate flag="feature_text_to_checklist"><MiniTile to="/tools/text-to-checklist" icon={ListChecks} bg="bg-[color:var(--primary-soft)]" ig="text-[color:var(--primary-ink)]" title="Text → List" desc="Prose in · checklist out." testid="tile-t2c" /></FeatureGate>
          <FeatureGate flag="feature_url_summary"><MiniTile to="/tools/url-summary" icon={FileText} bg="bg-[color:var(--warning-bg)]" ig="text-[color:var(--warning)]" title="URL Summary" desc="TL;DR any article." testid="tile-us" /></FeatureGate>
          <FeatureGate flag="feature_ocr_tool"><MiniTile to="/tools/ocr" icon={ScanText} bg="bg-[color:var(--accent-soft)]" ig="text-[color:var(--accent-ink)]" title="Image → Text" desc="Extract text from any photo." testid="tile-ocr" /></FeatureGate>
          <FeatureGate flag="feature_contextual_memory"><MiniTile to="/memory" icon={Brain} bg="bg-[color:var(--primary-soft)]" ig="text-[color:var(--primary-ink)]" title="Memory" desc="Search everything you've captured." testid="tile-memory" /></FeatureGate>
        </div>

        {/* In-feed ad — mobile 300×250, desktop 728×90 */}
        <div className="md:col-span-8 lg:col-span-12 my-2">
          <div className="md:hidden"><AdSlot format="in-feed" testid="ad-in-feed-mobile" /></div>
          <div className="hidden md:block xl:hidden"><AdSlot format="desktop-inline" testid="ad-in-feed-desktop" /></div>
          {/* On xl+ screens the right-rail sidebar ad already shows, so skip the in-feed banner to avoid ad sprawl */}
        </div>

        {/* How it works row */}
        <div className="md:col-span-8 lg:col-span-12 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Camera, k: "Capture", t: "Snap a label or upload any photo. Our vision model reads name, dosage and schedule." },
              { icon: Bell, k: "Remind", t: "Get a push notification (even on iPhone when installed as PWA) or an in-app alert before each dose." },
              { icon: FileDown, k: "Export", t: "Send your schedule to Google / Apple / Samsung calendars, or print a signed PDF with a QR to the app." },
            ].map(({ icon: Icon, k, t }, i) => (
              <div key={k} className="card-tactile rounded-xl p-5 animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="h-4 w-4 text-[color:var(--primary-ink)]" />
                  <span className="label-caps">{k}</span>
                </div>
                <p className="text-sm text-[color:var(--text-main)] leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
